import React, { useEffect, useState } from 'react';
import usePersistedState from '../usePersistedState';
import axios from 'axios';
import toast from 'react-hot-toast';

const API = axios.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
const ATTRS = ['sweet','salty','sour','bitter','umami','heat','aroma_intensity'];
const ATTR_LABELS = { sweet:'Sweet', salty:'Salty', sour:'Sour', bitter:'Bitter', umami:'Umami', heat:'Heat', aroma_intensity:'Aroma' };
const ATTR_COLORS = { sweet:'#e91e63', salty:'#2196f3', sour:'#cddc39', bitter:'#795548', umami:'#9c27b0', heat:'#f44336', aroma_intensity:'#ff9800' };

function ProfileBar({ profile }) {
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
      {ATTRS.map(a => (
        <div key={a} style={{ textAlign: 'center', minWidth: 52 }}>
          <div style={{ height: 46, width: 14, background: 'var(--bg-input, #f0f0f0)', borderRadius: 7, margin: '0 auto', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: `${Math.min(100,(profile[a]||0)*10)}%`, background: ATTR_COLORS[a], borderRadius: 7, transition: 'height 0.3s' }} />
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, marginTop: 3 }}>{(profile[a]||0).toFixed(1)}</div>
          <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>{ATTR_LABELS[a]}</div>
        </div>
      ))}
    </div>
  );
}

export default function FlavorLab() {
  const [tab, setTab] = usePersistedState('flavorlab.tab', 'seasonings');

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Flavor Lab</div>
          <div className="page-subtitle">R&D flavor data — base ingredients & seasoning composition</div>
        </div>
      </div>
      <div className="tabs" style={{ marginBottom: 16 }}>
        <button className={`tab ${tab==='seasonings'?'active':''}`} onClick={()=>setTab('seasonings')}>Seasoning Builder</button>
        <button className={`tab ${tab==='rm'?'active':''}`} onClick={()=>setTab('rm')}>Raw Materials</button>
        <button className={`tab ${tab==='notes'?'active':''}`} onClick={()=>setTab('notes')}>Item Profiles</button>
      </div>
      {tab==='seasonings' ? <SeasoningBuilder /> : tab==='rm' ? <SeasoningNotes classification="RAW MATERIALS" includeProducts={false} /> : <SeasoningNotes />}
    </div>
  );
}

// ─── TAB 2: SEASONING BUILDER ───
function SeasoningBuilder() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [baseList, setBaseList] = useState([]);
  const [components, setComponents] = useState([]);
  const [derived, setDerived] = useState(null);
  const [totalPct, setTotalPct] = useState(0);
  const [status, setStatus] = useState({ withComposition: [], withManual: [] });
  const [proposal, setProposal] = useState(null);
  const [deriving, setDeriving] = useState(false);
  const specFileRef = React.useRef(null);

  const deriveFromSpec = async (file) => {
    if (!file || !selected) return;
    setDeriving(true); setProposal(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await API.post(`/flavor/seasonings/${selected.item_code}/derive-from-spec`, fd);
      setProposal(r.data.proposal);
      toast.success('Proposal ready — review and apply');
    } catch (e) { toast.error(e.response?.data?.error || 'Derive failed'); }
    setDeriving(false);
    if (specFileRef.current) specFileRef.current.value = '';
  };

  const applyProposal = async () => {
    if (!proposal || !selected) return;
    try {
      const r = await API.post(`/flavor/seasonings/${selected.item_code}/apply-proposal`, proposal);
      toast.success('Composition applied' + (r.data.created?.length ? ` — ${r.data.created.length} new base ingredient(s) created` : ''));
      setProposal(null);
      // reload composition + bases + derived
      API.get('/flavor/base-ingredients').then(x => setBaseList(x.data)).catch(()=>{});
      loadSeasoning(selected);
    } catch (e) { toast.error(e.response?.data?.error || 'Apply failed'); }
  };


  useEffect(() => {
    API.get('/items', { params: { classification: 'INGREDIENTS' } }).then(r => setItems(r.data)).catch(()=>{});
    API.get('/flavor/base-ingredients').then(r => setBaseList(r.data)).catch(()=>{});
    API.get('/flavor/seasonings-status').then(r => setStatus(r.data)).catch(()=>{});
  }, []);

  const filtered = search.length > 1
    ? items.filter(i => (i.item_code+' '+i.item_name).toLowerCase().includes(search.toLowerCase())).slice(0, 12)
    : [];

  const loadSeasoning = async (item) => {
    setSelected(item); setSearch('');
    const r = await API.get(`/flavor/seasoning/${encodeURIComponent(item.item_code)}`);
    setComponents(r.data.composition.map(c => ({ base_ingredient_id: c.base_ingredient_id, name: c.name, percentage: c.percentage })));
    setDerived(r.data.derived); setTotalPct(r.data.totalPct);
  };

  const recompute = (comps) => {
    const d = {}; ATTRS.forEach(a => d[a] = 0);
    let tp = 0;
    comps.forEach(c => {
      const b = baseList.find(x => x.id === c.base_ingredient_id);
      if (!b) return;
      tp += (c.percentage||0);
      ATTRS.forEach(a => { d[a] += (b[a]||0) * (c.percentage||0) / 100; });
    });
    ATTRS.forEach(a => d[a] = Math.round(d[a]*100)/100);
    setDerived(d); setTotalPct(Math.round(tp*100)/100);
  };

  const addComponent = (baseId) => {
    if (!baseId) return;
    if (components.find(c => c.base_ingredient_id === parseInt(baseId))) return toast.error('Already added');
    const b = baseList.find(x => x.id === parseInt(baseId));
    const next = [...components, { base_ingredient_id: b.id, name: b.name, percentage: 0 }];
    setComponents(next); recompute(next);
  };

  const setPct = (idx, pct) => {
    const next = components.map((c, i) => i === idx ? { ...c, percentage: parseFloat(pct)||0 } : c);
    setComponents(next); recompute(next);
  };

  const removeComp = (idx) => {
    const next = components.filter((_, i) => i !== idx);
    setComponents(next); recompute(next);
  };

  const save = async () => {
    if (!selected) return;
    await API.put(`/flavor/seasoning/${encodeURIComponent(selected.item_code)}`, { components });
    toast.success('Composition saved');
    API.get('/flavor/seasonings-status').then(r => setStatus(r.data)).catch(()=>{});
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-title">Select Seasoning / Ingredient</div>
        <div style={{ position: 'relative', maxWidth: 480 }}>
          <input className="form-input" style={{ width: '100%' }} placeholder="Search item code or name..."
            value={search} onChange={e => setSearch(e.target.value)} />
          {filtered.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg-card, #fff)', border: '1px solid var(--border)', borderRadius: 8, zIndex: 30, maxHeight: 260, overflowY: 'auto', boxShadow: '0 4px 14px rgba(0,0,0,0.12)' }}>
              {filtered.map(i => (
                <div key={i.id} onClick={() => loadSeasoning(i)}
                  style={{ padding: '7px 10px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                  <span className="mono" style={{ color: 'var(--accent)', fontWeight: 700 }}>{i.item_code}</span> {i.item_name}
                  {status.withComposition.includes(i.item_code) && <span style={{ marginLeft: 6, fontSize: 9, background: '#4caf50', color: '#fff', borderRadius: 8, padding: '1px 6px' }}>✓ profiled</span>}
                </div>
              ))}
            </div>
          )}
        </div>
        {selected && <div style={{ marginTop: 10, fontWeight: 700 }}>{selected.item_code} — {selected.item_name}</div>}
      </div>

      {selected && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div className="card">
            <div className="card-title">Composition (from Base Master)</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '8px 0 12px' }}>
            <input ref={specFileRef} type="file" accept=".pdf" style={{ display: 'none' }}
              onChange={e => deriveFromSpec(e.target.files?.[0])} />
            <button className="btn btn-secondary btn-sm" disabled={deriving} onClick={() => specFileRef.current?.click()}>
              {deriving ? '🤖 Analyzing supplier spec...' : '📄 Derive from Supplier Spec (PDF)'}
            </button>
          </div>
          {proposal && (
            <div style={{ border: '2px solid var(--accent)', borderRadius: 10, padding: 12, marginBottom: 14, background: 'var(--bg-hover, #faf5fa)' }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>🤖 Proposed composition {proposal.confidence ? `(confidence: ${proposal.confidence})` : ''}</div>
              {proposal.anchors && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>Anchors: {proposal.anchors}</div>}
              <table style={{ width: '100%', fontSize: 12, marginBottom: 8 }}>
                <thead><tr><th style={{textAlign:'left'}}>Base Ingredient</th><th style={{textAlign:'left'}}>From spec</th><th style={{width:90}}>%</th></tr></thead>
                <tbody>
                  {proposal.components.map((cm, i) => (
                    <tr key={'c'+i}>
                      <td>{(baseList.find(b => b.id === cm.base_ingredient_id) || {}).name || cm.base_ingredient_id}</td>
                      <td style={{ color: 'var(--text-muted)' }}>{cm.source_name}</td>
                      <td><input type="number" className="form-input" style={{ width: 80 }} value={cm.percentage}
                        onChange={e => setProposal(p => ({ ...p, components: p.components.map((x, j) => j === i ? { ...x, percentage: parseFloat(e.target.value) || 0 } : x) }))} /></td>
                    </tr>
                  ))}
                  {(proposal.new_ingredients || []).map((n, i) => (
                    <tr key={'n'+i} style={{ background: '#fff8e1' }}>
                      <td><b>NEW:</b> {n.name}</td>
                      <td style={{ color: 'var(--text-muted)' }}>{n.source_name}</td>
                      <td><input type="number" className="form-input" style={{ width: 80 }} value={n.percentage}
                        onChange={e => setProposal(p => ({ ...p, new_ingredients: p.new_ingredients.map((x, j) => j === i ? { ...x, percentage: parseFloat(e.target.value) || 0 } : x) }))} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
                Total: {(proposal.components.reduce((s, x) => s + x.percentage, 0) + (proposal.new_ingredients || []).reduce((s, x) => s + x.percentage, 0)).toFixed(1)}%
                {' · '}New ingredients get created in the Base Master with AI-estimated flavor attributes (editable later in Master Data).
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={applyProposal}>✓ Apply Composition</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setProposal(null)}>Discard</button>
              </div>
            </div>
          )}
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <select className="form-input" defaultValue="" onChange={e => { addComponent(e.target.value); e.target.value=''; }} style={{ flex: 1 }}>
                <option value="" disabled>+ Add base ingredient...</option>
                {baseList.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <table style={{ width: '100%' }}>
              <thead><tr><th>Base Ingredient</th><th style={{ width: 90, textAlign: 'center' }}>%</th><th style={{ width: 36 }}></th></tr></thead>
              <tbody>
                {components.map((c, idx) => (
                  <tr key={c.base_ingredient_id}>
                    <td style={{ fontWeight: 600 }}>{c.name}</td>
                    <td style={{ textAlign: 'center' }}>
                      <input type="number" min="0" max="100" step="0.1" className="form-input" value={c.percentage}
                        onChange={e => setPct(idx, e.target.value)} style={{ width: 70, textAlign: 'center' }} />
                    </td>
                    <td><button className="btn btn-danger btn-sm" onClick={() => removeComp(idx)}>×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: Math.abs(totalPct-100) < 0.01 ? 'var(--green, #2e7d32)' : 'var(--amber, #b26a00)' }}>
                Total: {totalPct}% {Math.abs(totalPct-100) >= 0.01 && '(should be 100%)'}
              </div>
              <button className="btn btn-primary btn-sm" onClick={save}>💾 Save Composition</button>
            </div>
          </div>

          <div className="card">
            <div className="card-title">Derived Flavor Profile</div>
            {derived ? <ProfileBar profile={derived} /> : <div className="text-muted" style={{ fontSize: 12 }}>Add components to see the predicted profile</div>}
            <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-muted)' }}>
              Computed from base ingredient values weighted by composition %. This is the theoretical prediction — panel data will validate it over time.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── TAB: SEASONING NOTES (derived vs sensory judgment + spider) ───
function SpiderChart({ series }) {
  // series: [{ label, color, values: {attr: n} }]
  const size = 260, cx = size/2, cy = size/2, R = 95;
  const n = ATTRS.length;
  const angle = (i) => (Math.PI * 2 * i / n) - Math.PI / 2;
  const pt = (i, v) => [cx + Math.cos(angle(i)) * R * (v/10), cy + Math.sin(angle(i)) * R * (v/10)];
  const ringPath = (frac) => ATTRS.map((_, i) => {
    const [x, y] = [cx + Math.cos(angle(i)) * R * frac, cy + Math.sin(angle(i)) * R * frac];
    return `${i === 0 ? 'M' : 'L'}${x},${y}`;
  }).join(' ') + ' Z';
  return (
    <svg width={size} height={size} style={{ maxWidth: '100%' }}>
      {[0.25, 0.5, 0.75, 1].map(f => (
        <path key={f} d={ringPath(f)} fill="none" stroke="var(--border, #e3d5e0)" strokeWidth="1" />
      ))}
      {ATTRS.map((a, i) => {
        const [x, y] = [cx + Math.cos(angle(i)) * R, cy + Math.sin(angle(i)) * R];
        const [lx, ly] = [cx + Math.cos(angle(i)) * (R + 18), cy + Math.sin(angle(i)) * (R + 18)];
        return (
          <g key={a}>
            <line x1={cx} y1={cy} x2={x} y2={y} stroke="var(--border, #e3d5e0)" strokeWidth="1" />
            <text x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 9, fill: 'var(--text-muted, #888)', fontWeight: 700 }}>{ATTR_LABELS[a]}</text>
          </g>
        );
      })}
      {series.map((s, si) => (
        <g key={si}>
          <path d={ATTRS.map((a, i) => { const [x, y] = pt(i, s.values[a] || 0); return `${i === 0 ? 'M' : 'L'}${x},${y}`; }).join(' ') + ' Z'}
            fill={s.color} fillOpacity="0.18" stroke={s.color} strokeWidth="2" />
          {ATTRS.map((a, i) => { const [x, y] = pt(i, s.values[a] || 0); return <circle key={a} cx={x} cy={y} r="3" fill={s.color} />; })}
        </g>
      ))}
    </svg>
  );
}

function SeasoningNotes({ classification = 'INGREDIENTS,RAW MATERIALS', includeProducts = true }) { // Item Profiles: flavor profile for any item
  const [items, setItems] = useState([]);
  const [subtype, setSubtype] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [subTab, setSubTab] = useState('derived');
  const [derived, setDerived] = useState(null);
  const [hasComposition, setHasComposition] = useState(false);
  const [judgment, setJudgment] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const params = { classification };
    if (subtype) params.rm_subtype = subtype;
    const calls = [API.get('/items', { params })];
    if (includeProducts) calls.push(API.get('/products'));
    Promise.all(calls).then(([ri, rp]) => {
      const masterItems = ri.data.map(i => ({ ...i, _type: 'item' }));
      const products = (includeProducts && rp) ? (rp.data || []).filter(p => p.product_code).map(p => ({
        id: 'p' + p.id, item_code: p.product_code, item_name: p.name, _type: p.is_sfg ? 'SFG' : 'FG'
      })) : [];
      setItems([...products, ...masterItems]);
    }).catch(() => {});
  }, [classification, includeProducts, subtype]);

  const filtered = search.length > 1
    ? items.filter(i => (i.item_code + ' ' + i.item_name).toLowerCase().includes(search.toLowerCase())).slice(0, 14)
    : [];

  const loadSeasoning = async (item) => {
    setSelected(item); setSearch(''); setSubTab('derived');
    if (item._type === 'FG' || item._type === 'SFG') {
      // Product: resolve recursively from its recipe chain
      const r = await API.get(`/flavor/resolve/${encodeURIComponent(item.item_code)}`);
      setDerived(r.data.profile);
      setHasComposition(r.data.source === 'recipe');
      const m = {}; ATTRS.forEach(a => m[a] = r.data.profile[a] || 0);
      m.notes = '';
      setJudgment(m); setSaved(false);
      return;
    }
    const r = await API.get(`/flavor/seasoning/${encodeURIComponent(item.item_code)}`);
    setDerived(r.data.derived);
    setHasComposition((r.data.composition || []).length > 0);
    if (r.data.manual) {
      const m = {}; ATTRS.forEach(a => m[a] = r.data.manual[a] || 0);
      m.notes = r.data.manual.notes || '';
      setJudgment(m); setSaved(true);
    } else {
      // Pre-load judgment from derived as a starting point
      const m = {}; ATTRS.forEach(a => m[a] = r.data.derived[a] || 0);
      m.notes = '';
      setJudgment(m); setSaved(false);
    }
  };

  const saveJudgment = async () => {
    await API.put(`/flavor/seasoning/${encodeURIComponent(selected.item_code)}/manual`, judgment);
    toast.success('Sensory judgment saved');
    setSaved(true);
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-title">Select Item (RM or Ingredient)</div>
        {classification === 'RAW MATERIALS' && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {['', 'PLAIN', 'VALUE ADDED'].map(s => (
              <button key={s || 'all'} className={`tab ${subtype === s ? 'active' : ''}`} style={{ fontSize: 11, padding: '4px 12px' }}
                onClick={() => setSubtype(s)}>{s || 'All'}</button>
            ))}
          </div>
        )}
        <div style={{ position: 'relative', maxWidth: 480 }}>
          <input className="form-input" style={{ width: '100%' }} placeholder="Search item code or name..."
            value={search} onChange={e => setSearch(e.target.value)} />
          {filtered.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg-card, #fff)', border: '1px solid var(--border)', borderRadius: 8, zIndex: 30, maxHeight: 260, overflowY: 'auto', boxShadow: '0 4px 14px rgba(0,0,0,0.12)' }}>
              {filtered.map(i => (
                <div key={i.id} onClick={() => loadSeasoning(i)}
                  style={{ padding: '7px 10px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
                  <span className="mono" style={{ color: 'var(--accent)', fontWeight: 700 }}>{i.item_code}</span> {i.item_name}
                  {(i._type === 'FG' || i._type === 'SFG') && <span style={{ marginLeft: 6, fontSize: 9, background: i._type==='FG' ? '#7B2D6B' : '#2196f3', color: '#fff', borderRadius: 8, padding: '1px 6px', fontWeight: 700 }}>{i._type}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
        {selected && <div style={{ marginTop: 10, fontWeight: 700 }}>{selected.item_code} — {selected.item_name}</div>}
      </div>

      {selected && judgment && (
        <div className="card">
          <div className="tabs" style={{ marginBottom: 14 }}>
            <button className={`tab ${subTab==='derived'?'active':''}`} onClick={() => setSubTab('derived')}>Derived (Theory)</button>
            <button className={`tab ${subTab==='judgment'?'active':''}`} onClick={() => setSubTab('judgment')}>Sensory Judgment</button>
            <button className={`tab ${subTab==='compare'?'active':''}`} onClick={() => setSubTab('compare')}>Comparison</button>
          </div>

          {subTab === 'derived' && (
            <div>
              {hasComposition ? (
                <div style={{ display: 'flex', gap: 30, alignItems: 'center', flexWrap: 'wrap' }}>
                  <SpiderChart series={[{ label: 'Derived', color: '#7B2D6B', values: derived }]} />
                  <ProfileBar profile={derived} />
                </div>
              ) : (
                <div className="text-muted" style={{ fontSize: 13 }}>No composition entered for this seasoning yet — build it in the Seasoning Builder tab to get a derived profile.</div>
              )}
            </div>
          )}

          {subTab === 'judgment' && (
            <div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                {saved ? 'Saved sensory judgment — edit and re-save anytime.' : 'Pre-loaded from the derived profile — adjust to match your actual tasting, then save.'}
              </div>
              <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '8px 14px', alignItems: 'center' }}>
                  {ATTRS.map(a => (
                    <React.Fragment key={a}>
                      <label style={{ fontSize: 12, fontWeight: 700, color: ATTR_COLORS[a] }}>{ATTR_LABELS[a]}</label>
                      <input type="number" min="0" max="10" step="0.5" className="form-input" style={{ width: 70, textAlign: 'center' }}
                        value={judgment[a]} onChange={e => setJudgment({ ...judgment, [a]: parseFloat(e.target.value) || 0 })} />
                    </React.Fragment>
                  ))}
                </div>
                <SpiderChart series={[{ label: 'Judgment', color: '#2196f3', values: judgment }]} />
              </div>
              <div style={{ marginTop: 12 }}>
                <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Notes</label>
                <textarea className="form-input" style={{ width: '100%', minHeight: 60 }} value={judgment.notes}
                  onChange={e => setJudgment({ ...judgment, notes: e.target.value })} placeholder="Tasting observations, character notes..." />
              </div>
              <button className="btn btn-primary" style={{ marginTop: 10 }} onClick={saveJudgment}>💾 Save Sensory Judgment</button>
            </div>
          )}

          {subTab === 'compare' && (
            <div style={{ display: 'flex', gap: 30, alignItems: 'center', flexWrap: 'wrap' }}>
              <SpiderChart series={[
                { label: 'Derived', color: '#7B2D6B', values: derived || {} },
                { label: 'Judgment', color: '#2196f3', values: judgment }
              ]} />
              <div>
                <div style={{ display: 'flex', gap: 14, marginBottom: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#7B2D6B' }}>■ Derived (theory)</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#2196f3' }}>■ Sensory judgment</span>
                </div>
                <table>
                  <thead><tr><th>Attribute</th><th style={{textAlign:'center'}}>Derived</th><th style={{textAlign:'center'}}>Judgment</th><th style={{textAlign:'center'}}>Δ</th></tr></thead>
                  <tbody>
                    {ATTRS.map(a => {
                      const d = derived?.[a] || 0, j = judgment[a] || 0, diff = Math.round((j - d) * 100) / 100;
                      return (
                        <tr key={a}>
                          <td style={{ fontWeight: 600, color: ATTR_COLORS[a] }}>{ATTR_LABELS[a]}</td>
                          <td style={{ textAlign: 'center' }}>{d}</td>
                          <td style={{ textAlign: 'center' }}>{j}</td>
                          <td style={{ textAlign: 'center', fontWeight: 700, color: Math.abs(diff) >= 2 ? 'var(--red, #c62828)' : Math.abs(diff) >= 1 ? 'var(--amber, #b26a00)' : 'var(--green, #2e7d32)' }}>{diff > 0 ? '+' : ''}{diff}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
